// MDXEditor parses raw HTML inside markdown as JSX and recreates it with
// document.createElement + setAttribute for every attribute (GenericHTMLNode).
// Stored JD / PAIC markdown is user- and AI-authored, so this visitor runs before
// MDXEditor's own HTML visitor and enforces an allowlist (UI-1): only formatting
// tags with a small set of attributes reach the DOM; everything else is removed.

// Formatting tags we render. u/sup/sub/code/span are also what MDXEditor itself
// writes back when a user edits (LexicalTextVisitor), so they must round-trip.
// <img> never reaches this visitor: MDXEditor's image plugin handles it.
export const ALLOWED_HTML_TAGS = new Set([
  "p", "span", "strong", "b", "em", "i", "u", "sup", "sub", "br",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
  "a", "table", "thead", "tbody", "tr", "th", "td",
  "blockquote", "code", "pre",
]);

// Attributes allowed per tag. Anything not listed is dropped.
const ALLOWED_ATTRIBUTES = {
  a: new Set(["href", "title"]),
  span: new Set(["style"]),
  td: new Set(["colspan", "rowspan"]),
  th: new Set(["colspan", "rowspan"]),
  ol: new Set(["start"]),
};

// Not allowed and their content is not text worth keeping (or is executable):
// drop the element together with its children. Other disallowed tags are unwrapped
// so the user's text survives without the tag.
export const DROP_WITH_CONTENT_TAGS = new Set([
  "script", "style", "iframe", "embed", "object", "param", "template", "noscript",
  "title", "head", "meta", "link", "base", "textarea", "select", "option",
  "optgroup", "datalist", "canvas", "audio", "video", "source", "track",
]);

// Colour/emphasis only — no positioning, sizing or url() that could overlay the UI.
const SAFE_STYLE_PROPERTIES = new Set([
  "color", "background-color", "font-weight", "font-style", "text-decoration",
]);
const UNSAFE_STYLE_VALUE = /url\(|expression\(|javascript:|[<>\\]/i;

const UNSAFE_URL = /^(javascript|vbscript|data):/i;
const NUMERIC = /^\d{1,3}$/;

const isSafeUrl = (value) => {
  // Browsers ignore whitespace/control chars inside the scheme ("java\tscript:").
  const normalized = Array.from(value).filter((ch) => ch.charCodeAt(0) > 0x20).join("");
  return !UNSAFE_URL.test(normalized);
};

// Keeps only allowlisted CSS declarations; returns "" when none survive.
export const sanitizeStyle = (value) =>
  String(value)
    .split(";")
    .map((declaration) => declaration.trim())
    .filter((declaration) => {
      const colon = declaration.indexOf(":");
      if (colon < 1) return false;
      const property = declaration.slice(0, colon).trim().toLowerCase();
      const propertyValue = declaration.slice(colon + 1);
      return SAFE_STYLE_PROPERTIES.has(property) && !UNSAFE_STYLE_VALUE.test(propertyValue);
    })
    .join("; ");

// Returns the attribute to keep (possibly with a cleaned value), or null to drop it.
export const sanitizeHtmlAttribute = (attribute, tagName) => {
  // mdxJsxExpressionAttribute ({...spread}) and expression values ({…}) are never literal HTML.
  if (attribute?.type !== "mdxJsxAttribute" || typeof attribute.name !== "string") return null;
  if (attribute.value !== null && attribute.value !== undefined && typeof attribute.value !== "string") return null;

  const name = attribute.name.toLowerCase();
  if (!ALLOWED_ATTRIBUTES[tagName]?.has(name)) return null;
  const value = attribute.value ?? "";

  if (name === "href") return isSafeUrl(value) ? attribute : null;
  if (name === "colspan" || name === "rowspan" || name === "start") return NUMERIC.test(value) ? attribute : null;
  if (name === "style") {
    const style = sanitizeStyle(value);
    return style ? { ...attribute, value: style } : null;
  }
  return attribute;
};

// Builds the import visitor. `testNode` is MDXEditor's isMdastHTMLNode, passed in
// so this module stays free of the ESM-only @mdxeditor/editor import (testable in Jest).
export const createSafeHtmlVisitor = (testNode) => ({
  testNode,
  // Above MdastHTMLVisitor (-100) and the formatting visitors (0) so it always runs first.
  priority: 100,
  visitNode({ mdastNode, actions, lexicalParent }) {
    const tagName = String(mdastNode.name).toLowerCase();
    if (DROP_WITH_CONTENT_TAGS.has(tagName)) {
      return;
    }
    if (!ALLOWED_HTML_TAGS.has(tagName)) {
      // Unwrap: drop the tag and its attributes, keep the text inside it.
      actions.visitChildren(mdastNode, lexicalParent);
      return;
    }
    mdastNode.attributes = (mdastNode.attributes ?? [])
      .map((attribute) => sanitizeHtmlAttribute(attribute, tagName))
      .filter(Boolean);
    actions.nextVisitor();
  },
});
